import { Link } from "wouter";
import { Footer } from "@/components/layout/footer";
import { RouteSeo } from "@/components/seo/route-seo";
import { benCareJsonLd, productJsonLd } from "@/lib/seo";

const currentBatchInventory = 14;
const currentBatchProductName = "Liquid Stretch First Light Harvest";
const currentBatchDescription =
  "Fourteen double-sized 4 oz bottles are available from Liquid Stretch First Light Harvest, the launch special.";

const homeFaqJsonLd = {
  "@type": "FAQPage",
  "@id": "https://liquidstretch.com/#faq",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is Liquid Stretch?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Liquid Stretch is a handcrafted Vermont hemp ingestible oil in organic MCT oil, made for stretching routines, mobility work, and slow body-care practices.",
      },
    },
    {
      "@type": "Question",
      name: "How limited is Liquid Stretch First Light Harvest?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Fourteen double-sized 4 oz bottles are available from Liquid Stretch First Light Harvest, the launch special.",
      },
    },
    {
      "@type": "Question",
      name: "What is Liquid Stretch Golden Hour Harvest?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Liquid Stretch Golden Hour Harvest is the planned next 2 oz formulation with about 58 servings and a 3:1 CBD-to-THC balance. Join the next-batch list to be notified when pre-orders open.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need to understand Lay Down Now or Enhanced Biofeedback first?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. Liquid Stretch provides the simple starting point. Lay Down Now and Enhanced Biofeedback carry the deeper practice library for people who want to keep learning.",
      },
    },
    {
      "@type": "Question",
      name: "Who is Ben Care?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ben Care is the founder voice connected to Liquid Stretch formulation work and the Vermont Forest ecosystem.",
      },
    },
  ],
};

export default function Home() {
  return (
    <div className="home-page">
      <RouteSeo
        title="Liquid Stretch | Vermont Hemp Oil for Stretching Rituals"
        description="Liquid Stretch First Light Harvest is available now for $90. Fourteen double-sized 4 oz launch bottles are available."
        path="/"
        type="product"
        jsonLd={[benCareJsonLd, productJsonLd, homeFaqJsonLd]}
      />
      <style>{`
        :root {
          --forest: #05170d;
          --forest-2: #082415;
          --forest-3: #123820;
          --moss: #315b35;
          --gold: #d69b13;
          --gold-2: #f0c766;
          --cream: #f7edd7;
          --paper: #fff8e9;
          --muted: #cfc3aa;
          --line: rgba(240, 199, 102, 0.42);
          --shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
        }

        .home-page {
          min-height: 100vh;
          background:
            radial-gradient(circle at 50% 0%, rgba(214, 155, 19, 0.18), transparent 34rem),
            linear-gradient(180deg, #020906 0%, var(--forest) 42%, #071009 100%);
          color: var(--cream);
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        .campaign-strip {
          background: linear-gradient(90deg, #082415, #0a2b18);
          border-bottom: 1px solid var(--line);
          color: var(--cream);
          text-align: center;
          padding: 0.95rem 1rem;
          font-weight: 850;
          line-height: 1.45;
        }

        .campaign-strip span {
          color: var(--gold-2);
        }

        .inventory-counter {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 16px;
          align-items: center;
          border: 1px solid var(--line);
          border-radius: 8px;
          background:
            radial-gradient(circle at 0% 0%, rgba(240, 199, 102, 0.16), transparent 16rem),
            rgba(255, 248, 233, 0.055);
          padding: 18px;
          margin: 24px 0 0;
        }

        .inventory-count {
          display: grid;
          place-items: center;
          width: 92px;
          min-height: 92px;
          border: 1px solid rgba(240, 199, 102, 0.64);
          border-radius: 8px;
          background: rgba(5, 23, 13, 0.72);
          box-shadow: inset 0 0 24px rgba(214, 155, 19, 0.12);
        }

        .inventory-count strong {
          color: var(--gold-2);
          font-family: Georgia, "Times New Roman", serif;
          font-size: 2.7rem;
          line-height: 1;
        }

        .inventory-count span,
        .inventory-copy span {
          color: var(--muted);
          font-size: 0.78rem;
          font-weight: 900;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .inventory-copy strong {
          display: block;
          color: var(--cream);
          font-size: 1.16rem;
          margin: 3px 0 5px;
        }

        .inventory-copy p {
          color: var(--muted);
          line-height: 1.5;
          margin: 0;
        }

        main {
          width: min(1180px, calc(100% - 32px));
          margin: 0 auto;
          padding: 42px 0 64px;
        }

        .hero {
          display: grid;
          grid-template-columns: minmax(0, 0.88fr) minmax(360px, 1.12fr);
          gap: 34px;
          align-items: center;
        }

        .eyebrow {
          display: inline-flex;
          align-items: center;
          border: 1px solid var(--line);
          border-radius: 999px;
          color: var(--gold-2);
          background: rgba(214, 155, 19, 0.1);
          padding: 0.48rem 0.76rem;
          font-size: 0.78rem;
          font-weight: 900;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        h1 {
          color: var(--cream);
          font-family: Georgia, "Times New Roman", serif;
          font-size: clamp(3.2rem, 9vw, 7.2rem);
          line-height: 0.88;
          letter-spacing: 0.02em;
          margin: 0 0 20px;
          text-shadow: 0 8px 34px rgba(0, 0, 0, 0.5);
        }

        .lead {
          display: grid;
          gap: 0.85rem;
          color: #eadfc6;
          font-size: clamp(1.1rem, 2vw, 1.35rem);
          line-height: 1.55;
          max-width: 640px;
          margin: 0 0 24px;
        }

        .lead span {
          display: block;
        }

        .hero-actions {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          margin-top: 24px;
        }

        .hero-proof {
          border-left: 2px solid rgba(240, 199, 102, 0.68);
          margin: 4px 0 2px;
          padding: 4px 0 4px 18px;
          max-width: 620px;
        }

        .hero-proof blockquote {
          color: var(--cream);
          font-family: Georgia, "Times New Roman", serif;
          font-size: clamp(1.02rem, 1.55vw, 1.18rem);
          line-height: 1.45;
          margin: 0 0 8px;
        }

        .hero-proof p {
          margin: 0;
        }

        .button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 54px;
          border-radius: 6px;
          padding: 0.9rem 1.1rem;
          border: 1px solid var(--gold);
          font-weight: 900;
          cursor: pointer;
          text-align: center;
        }

        .button.primary {
          color: #150f04;
          background: linear-gradient(180deg, var(--gold-2), var(--gold));
          box-shadow: 0 12px 34px rgba(214, 155, 19, 0.23);
        }

        .button.secondary {
          color: var(--gold-2);
          background: rgba(5, 23, 13, 0.65);
        }

        .button:disabled,
        .button[aria-disabled="true"] {
          cursor: not-allowed;
          opacity: 0.62;
          box-shadow: none;
        }

        .button.sold-out {
          color: var(--cream);
          background: rgba(255, 248, 233, 0.08);
          border-color: rgba(240, 199, 102, 0.38);
          cursor: default;
        }

        .label-frame {
          position: relative;
          display: grid;
          place-items: center;
          border: 1px solid var(--line);
          border-radius: 8px;
          background:
            radial-gradient(circle at 50% 8%, rgba(240, 199, 102, 0.14), transparent 22rem),
            rgba(2, 9, 6, 0.7);
          box-shadow: var(--shadow);
          padding: 10px;
          overflow: hidden;
        }

        .label-frame img {
          display: block;
          width: min(100%, 430px);
          height: auto;
          border-radius: 5px;
          filter: drop-shadow(0 28px 70px rgba(0, 0, 0, 0.48));
        }

        .status-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
          margin: 34px 0;
        }

        .status {
          border: 1px solid var(--line);
          border-radius: 8px;
          background: rgba(255, 248, 233, 0.06);
          padding: 18px;
        }

        .status strong {
          display: block;
          color: var(--gold-2);
          font-size: 1.55rem;
          line-height: 1.1;
          margin-bottom: 8px;
        }

        .status span {
          color: var(--muted);
          line-height: 1.5;
        }

        .method-section,
        .testimonials {
          margin-top: 34px;
        }

        .section-heading {
          max-width: 760px;
          margin-bottom: 18px;
        }

        .section-heading h2 {
          color: var(--gold-2);
          font-size: clamp(1.7rem, 3vw, 2.35rem);
          margin: 0 0 10px;
          font-family: Georgia, "Times New Roman", serif;
          letter-spacing: 0.02em;
        }

        .section-heading p {
          color: var(--muted);
          line-height: 1.65;
          margin: 0;
        }

        .method-layout {
          display: grid;
          gap: 16px;
        }

        .method-flow-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
          align-items: stretch;
        }

        .method-card,
        .quote-card {
          display: block;
          border: 1px solid var(--line);
          border-radius: 8px;
          background: linear-gradient(180deg, rgba(255, 248, 233, 0.075), rgba(255, 248, 233, 0.035));
          padding: 22px;
        }

        .method-step-card {
          display: grid;
          grid-template-rows: auto minmax(3.2rem, auto) minmax(5.8rem, auto) auto minmax(1.35rem, auto);
          gap: 12px;
          align-content: start;
          padding: 20px;
          background:
            linear-gradient(180deg, rgba(240, 199, 102, 0.08), transparent 48%),
            rgba(255, 248, 233, 0.045);
        }

        .method-step-heading {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 12px;
          align-items: start;
        }

        .method-index {
          display: grid;
          place-items: center;
          width: 36px;
          height: 36px;
          border: 1px solid rgba(240, 199, 102, 0.62);
          border-radius: 999px;
          color: var(--gold-2);
          background: rgba(5, 23, 13, 0.78);
          font-size: 0.95rem;
          font-weight: 950;
          line-height: 1;
        }

        .method-copy {
          min-width: 0;
        }

        .method-detail {
          min-width: 0;
          padding-top: 12px;
          border-top: 1px solid rgba(240, 199, 102, 0.22);
        }

        .use-banner {
          display: grid;
          grid-template-columns: minmax(260px, 0.74fr) minmax(0, 1.26fr);
          gap: 24px;
          align-items: center;
          padding: 26px;
          background:
            radial-gradient(circle at 7% 18%, rgba(240, 199, 102, 0.16), transparent 20rem),
            linear-gradient(180deg, rgba(255, 248, 233, 0.088), rgba(255, 248, 233, 0.035));
        }

        .use-banner h3 {
          color: var(--gold-2);
          font-family: Georgia, "Times New Roman", serif;
          font-size: clamp(1.55rem, 3vw, 2.2rem);
          letter-spacing: 0.02em;
          line-height: 1.08;
          margin: 0 0 12px;
          text-transform: none;
        }

        .use-banner p {
          color: var(--muted);
          line-height: 1.62;
          margin: 0;
        }

        .use-kicker {
          display: inline-flex;
          color: var(--gold-2);
          font-size: 0.78rem;
          font-weight: 950;
          letter-spacing: 0.13em;
          margin-bottom: 10px;
          text-transform: uppercase;
        }

        .use-points {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }

        .use-point {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 12px;
          align-items: start;
          border: 1px solid rgba(240, 199, 102, 0.24);
          border-radius: 8px;
          background: rgba(5, 23, 13, 0.5);
          padding: 10px 12px;
        }

        .use-point-mark {
          display: grid;
          place-items: center;
          width: 1.7rem;
          height: 1.7rem;
          border: 1px solid rgba(240, 199, 102, 0.72);
          border-radius: 999px;
          color: var(--gold-2);
          font-size: 0.78rem;
          font-weight: 950;
          line-height: 1;
        }

        .use-point strong {
          display: block;
          color: var(--cream);
          line-height: 1.28;
          margin-bottom: 2px;
        }

        .use-point span {
          color: var(--muted);
          font-size: 0.94rem;
          line-height: 1.35;
        }

        .method-card h3,
        .quote-card h3 {
          color: var(--gold-2);
          font-size: 1.05rem;
          margin: 0 0 6px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .method-role {
          display: inline-flex;
          color: var(--muted);
          font-size: 0.78rem;
          font-weight: 950;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .method-card h4 {
          color: var(--cream);
          font-family: Georgia, "Times New Roman", serif;
          font-size: clamp(1.12rem, 1.55vw, 1.34rem);
          line-height: 1.18;
          margin: 0;
        }

        .method-card p strong {
          color: var(--cream);
          font-weight: 850;
        }

        .method-card p,
        .method-card li,
        .quote-card blockquote,
        .quote-card p {
          color: var(--muted);
          line-height: 1.55;
        }

        .method-card p,
        .quote-card p {
          margin: 0;
        }

        .method-card ul,
        .method-card ol {
          display: grid;
          gap: 7px;
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .method-card li {
          position: relative;
          min-height: 1.45rem;
          padding-left: 1.35rem;
        }

        .method-list li::before {
          content: "";
          position: absolute;
          left: 0.08rem;
          top: 0.72em;
          width: 0.36rem;
          height: 0.36rem;
          border-radius: 999px;
          border: 1px solid rgba(240, 199, 102, 0.84);
          background: rgba(240, 199, 102, 0.18);
          box-shadow: 0 0 10px rgba(240, 199, 102, 0.18);
        }

        .method-steps {
          counter-reset: method-step;
        }

        .method-steps li {
          counter-increment: method-step;
          padding-left: 2.6rem;
        }

        .method-steps li::before {
          content: counter(method-step);
          position: absolute;
          left: 0;
          top: 0.16rem;
          display: grid;
          place-items: center;
          width: 1.7rem;
          height: 1.7rem;
          border: 1px solid var(--gold);
          border-radius: 999px;
          color: var(--gold-2);
          background: rgba(5, 23, 13, 0.72);
          font-size: 0.9rem;
          font-weight: 950;
          line-height: 1;
        }

        .method-steps-spaced {
          margin-top: 14px !important;
        }

        .method-card a {
          display: inline-flex;
          align-self: end;
          color: var(--gold-2);
          font-weight: 900;
          margin-top: 2px;
          font-size: 0.9rem;
        }

        .method-card-footer {
          min-height: 1.35rem;
        }

        .quote-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .quote-card blockquote {
          margin: 0 0 16px;
          font-size: 1.02rem;
          line-height: 1.62;
        }

        .quote-card.featured-quote {
          grid-column: 1 / -1;
        }

        .quote-meta {
          color: var(--gold-2);
          font-weight: 900;
        }

        .quote-location {
          color: var(--muted);
          font-size: 0.92rem;
          margin-left: 6px;
        }

        .note {
          color: var(--muted);
          font-size: 0.92rem;
          line-height: 1.55;
          margin-top: 12px;
        }

        .faq-section {
          margin-top: 34px;
          border: 1px solid var(--line);
          border-radius: 8px;
          background: rgba(255, 248, 233, 0.055);
          padding: 26px;
        }

        .faq-section h2 {
          color: var(--gold-2);
          margin: 0 0 16px;
          font-size: 1.45rem;
        }

        .faq-item p {
          color: var(--muted);
        }

        .faq-list {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .faq-item {
          border: 1px solid rgba(240, 199, 102, 0.24);
          border-radius: 8px;
          padding: 18px;
          background: rgba(5, 23, 13, 0.45);
        }

        .faq-item h3 {
          color: var(--cream);
          margin: 0 0 8px;
          font-size: 1rem;
        }


        a {
          color: var(--gold-2);
        }

        @media (max-width: 900px) {
          .hero,
          .status-grid,
          .method-flow-grid,
          .use-points,
          .method-step-card,
          .use-banner,
          .quote-grid,
          .faq-list,
          .method-index {
            width: 36px;
            height: 36px;
          }

          .method-detail {
            padding-top: 12px;
            border-top: 1px solid rgba(240, 199, 102, 0.22);
          }

          .method-step-card {
            grid-template-rows: auto;
          }

          .method-card.featured {
            grid-column: auto;
          }

          .hero-actions {
            grid-template-columns: 1fr;
          }

          .inventory-counter {
            grid-template-columns: 1fr;
          }

          main {
            width: min(100% - 20px, 680px);
            padding-top: 24px;
          }

          .label-frame img {
            width: min(100%, 360px);
          }
        }
      `}</style>

      <div className="campaign-strip">
        <span>{currentBatchInventory} First Light Harvest bottles are available now for $90.</span>{" "}
        Golden Hour Harvest remains on the next-batch list.
      </div>

      <main>
        <section className="hero" aria-label="Liquid Stretch campaign">
          <div>
            <span className="eyebrow">Handcrafted & lab verified</span>
            <h1>Liquid Stretch</h1>
            <p className="lead">
              <span>
                A small-run hemp ingestible oil for stretching routines,
                mobility work, and slow body-care practices.
              </span>
              <span>
                Liquid Stretch is made for adults who want a slower, more attentive
                way to pair hemp oil with body care.
              </span>
            </p>

            <aside className="hero-proof" aria-label="Featured customer note">
              <blockquote>
                "I was shocked. After about 2.5 hours, I was watching TV when I
                suddenly wanted to get down on the floor by the wood stove and
                stretch. I started doing cat-cow movements and just followed what
                felt good."
              </blockquote>
              <p><span className="quote-meta">Simone F.</span><span className="quote-location">Southern Vermont</span></p>
            </aside>

            <div className="hero-actions">
              <button
                className="button primary snipcart-add-item"
                data-item-id="1"
                data-item-name={currentBatchProductName}
                data-item-price="90.00"
                data-item-url="/api/products/1.json"
                data-item-description={currentBatchDescription}
                data-item-image="/images/liquid-stretch-v2-bottle-mockup.png"
                data-item-categories="4 oz. Launch Special"
                data-item-has-taxes-included="true"
                data-item-max-quantity={currentBatchInventory}
                data-item-shippable="true"
                type="button"
              >
                Purchase First Light Harvest — $90
              </button>
            </div>
            <p className="note">
              First Light Harvest is available for purchase now.
            </p>

          </div>

          <div className="label-frame">
            <img
              src="/images/liquid-stretch-v2-bottle-mockup.png"
              alt="Liquid Stretch First Light Harvest 4 oz launch bottle"
              width="896"
              height="1536"
              loading="eager"
            />
          </div>
        </section>

        <section className="status-grid" aria-label="Campaign status">
          <div className="status">
            <strong>116 experiences</strong>
            <span>First Light Harvest is labeled at about 116 experiences per bottle.</span>
          </div>
          <div className="status">
            <strong>3-4+ hours</strong>
            <span>The label frames this as a gradual, unfolding experience.</span>
          </div>
          <div className="status">
            <strong>THC 350mg / CBD 324mg</strong>
            <span>Per double-sized 4 fl oz bottle, with full-spectrum hemp extract in organic MCT oil.</span>
          </div>
        </section>

        <section className="method-section" aria-label="Liquid Stretch method">
          <div className="section-heading">
            <h2>How Enhanced Biofeedback is supported by Lay Down Now & Liquid Stretch</h2>
            <p>
              Liquid Stretch is designed for slow body-care sessions where less input,
              better attention, and gentle movement can work together. Lay Down Now
              settles the environment, Enhanced Biofeedback listens inward, and
              Follow That Feeling moves with the clearest body cue.
            </p>
          </div>

          <div className="method-layout">
            <article className="method-card use-banner">
              <div>
                <span className="use-kicker">Suggested Use</span>
                <h3>Start low. Give it time.</h3>
                <p>
                  Take a measured amount, give the oil time to come on, and keep the
                  session slow enough to notice how your body responds.
                </p>
              </div>
              <div className="use-points" aria-label="Suggested use highlights">
                <div className="use-point">
                  <span className="use-point-mark">1</span>
                  <div><strong>Shake well.</strong><span>Take ~1.0 g (~1/5 teaspoon).</span></div>
                </div>
                <div className="use-point">
                  <span className="use-point-mark">2</span>
                  <div><strong>Start low.</strong><span>Adjust dose as desired.</span></div>
                </div>
                <div className="use-point">
                  <span className="use-point-mark">3</span>
                  <div><strong>Allow 2-3 hours.</strong><span>The experience unfolds gradually.</span></div>
                </div>
              </div>
            </article>

            <div className="method-flow-grid">
              <article className="method-card method-step-card">
                <div className="method-step-heading">
                  <div className="method-index" aria-hidden="true">1</div>
                  <div>
                    <h3>Lay Down Now</h3>
                    <span className="method-role">The conditions</span>
                  </div>
                </div>
                <h4>Lower the sensory load.</h4>
                <p>
                  <strong>Lay Down Now</strong> lowers the sensory load. Lie down,
                  reduce incoming input, and let attention move from the room to
                  the body.
                </p>
                <div className="method-detail">
                  <ul className="method-list">
                    <li>Dim light and quiet sound.</li>
                    <li>Stop multitasking.</li>
                    <li>Let the body become easier to notice.</li>
                  </ul>
                </div>
                <div className="method-card-footer">
                  <a href="https://laydownnow.com/" target="_blank" rel="noopener noreferrer">
                    Learn the full Lay Down Now method
                  </a>
                </div>
              </article>

              <article className="method-card method-step-card">
                <div className="method-step-heading">
                  <div className="method-index" aria-hidden="true">2</div>
                  <div>
                    <h3>Enhanced Biofeedback</h3>
                    <span className="method-role">The attention technique</span>
                  </div>
                </div>
                <h4>Use interoception as the feedback loop.</h4>
                <p>
                  <strong>Interoception</strong> means sensing what is happening
                  inside your body. Treat those signals as feedback, not a command
                  to force an outcome.
                </p>
                <div className="method-detail">
                  <ul className="method-list">
                    <li>Notice pressure, tension, warmth, or ease.</li>
                    <li>Let attention refine before movement.</li>
                    <li>Shift from reacting to observing.</li>
                  </ul>
                </div>
                <div className="method-card-footer">
                  <a href="https://enhancedbiofeedback.com/" target="_blank" rel="noopener noreferrer">
                    Visit Enhanced Biofeedback
                  </a>
                </div>
              </article>

              <article className="method-card method-step-card">
                <div className="method-step-heading">
                  <div className="method-index" aria-hidden="true">3</div>
                  <div>
                    <h3>Follow That Feeling</h3>
                    <span className="method-role">The movement cue</span>
                  </div>
                </div>
                <h4>Use the body like a somatic tuning fork.</h4>
                <p>
                  <strong>Somatic</strong> means body-based. Let the clearest
                  internal cue choose the direction, then move slowly enough to
                  keep listening.
                </p>
                <div className="method-detail">
                  <ul className="method-list">
                    <li>Move slowly.</li>
                    <li>Stay with what feels good.</li>
                    <li>Do not force range.</li>
                  </ul>
                </div>
                <div className="method-card-footer" aria-hidden="true"></div>
              </article>
            </div>
          </div>
        </section>

        <section className="testimonials" aria-label="Customer comments">
          <div className="section-heading">
            <h2>What Our Customers Are Saying</h2>
          </div>

          <div className="quote-grid">
            <article className="quote-card featured-quote">
              <blockquote>
                "It was just a real relaxing day where I felt comfortable and got a
                lot of things done. To the point where I get things done that I've
                been wanting to get done for a long time. And it just automatically
                happened... I had probably one of the most productive days I had in
                months. And everything went easily."
              </blockquote>
              <p><span className="quote-meta">Fran A.</span><span className="quote-location">Vermont</span></p>
            </article>

            <article className="quote-card">
              <blockquote>
                "After 2.5 hours, I felt it 'kicking in' and a soothing calm came over
                me... To my surprise, I got down on the floor and stretched. I did
                some simple yoga moves (ie cat-cow, child and baby pose) and even
                made up a few poses."
              </blockquote>
              <p><span className="quote-meta">Simone F.</span><span className="quote-location">Southern Vermont</span></p>
            </article>

            <article className="quote-card">
              <blockquote>
                "Took an hour and a half to feel it, then 2 hours beginning of peak...
                Yours has a medicinal body effect and my favorite high of all the
                types of edibles I've tried."
              </blockquote>
              <p><span className="quote-meta">Johny A.</span><span className="quote-location">Vermont</span></p>
            </article>

            <article className="quote-card">
              <blockquote>
                "I love you. This is more than I was expecting."
              </blockquote>
              <p><span className="quote-meta">Tim</span><span className="quote-location">Vermont</span></p>
            </article>

            <article className="quote-card">
              <blockquote>
                "Relaxation came to me very naturally. It was very gentle."
              </blockquote>
              <p><span className="quote-meta">Madelyn E.</span><span className="quote-location">Delray Beach, Florida</span></p>
            </article>
          </div>
        </section>

        <section className="faq-section" id="faq" aria-label="Frequently Asked Questions">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            <article className="faq-item">
              <h3>What is Liquid Stretch?</h3>
              <p>
                Liquid Stretch is a handcrafted hemp ingestible oil in organic MCT
                oil, made for stretching routines, mobility work, and slow body-care
                practices.
              </p>
            </article>
            <article className="faq-item">
              <h3>How limited is First Light Harvest?</h3>
              <p>
                Fourteen double-sized 4 oz bottles are available from Liquid Stretch
                First Light Harvest, the launch special. When they are gone, this
                limited launch special closes.
              </p>
            </article>
            <article className="faq-item">
              <h3>What is Golden Hour Harvest?</h3>
              <p>
                Golden Hour Harvest is the planned next 2 oz formulation with about
                58 servings and a 3:1 CBD-to-THC balance. Join the next-batch list to
                be notified when it is ready and pre-orders open. No payment is taken now.
              </p>
            </article>
            <article className="faq-item">
              <h3>Do I need to understand Lay Down Now or EB first?</h3>
              <p>
                No. Liquid Stretch gives you the simple starting point here. Lay Down
                Now and Enhanced Biofeedback are there if you want the deeper practice
                after you understand the product.
              </p>
            </article>
            <article className="faq-item">
              <h3>Terms and policies</h3>
              <p>
                See the{" "}
                <Link href="/terms">Terms & Conditions page</Link>{" "}
                for shipping, returns, privacy, and support information.
              </p>
            </article>
          </div>
        </section>
      </main>

      <Footer variant="brand" />
    </div>
  );
}
